import React from 'react';
import { ArrowLeft, Package, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

function ReturnPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-semibold">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-8 h-8 text-orange-600" />
            <h1 className="text-3xl font-bold text-slate-900">Return Policy</h1>
          </div>
          <p className="text-slate-600 text-sm">Last Updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Overview */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Overview</h2>
            <p className="text-slate-700 leading-relaxed">
              At BrightBazaar.in, customer satisfaction is our top priority. We offer a hassle-free 
              return policy to ensure you have a great shopping experience. Please review our return 
              conditions carefully before initiating a return.
            </p>
          </section>

          {/* Return Window */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <Clock className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Return Window</h2>
                <p className="text-slate-700 leading-relaxed">
                  You may return most new, unopened items within <strong>30 days of delivery</strong> for 
                  a full refund. If the return is due to our error (you received an incorrect or defective 
                  item), we'll also cover the return shipping costs.
                </p>
              </div>
            </div>
          </section>

          {/* Eligible Items */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Eligible for Return</h2>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Items in original packaging with all tags and labels intact</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Unopened electronics with original seals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Furniture items in original condition (assembly not attempted)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Fashion items unworn, unwashed, with original tags</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Defective or damaged items received</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Non-Returnable Items */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <XCircle className="w-6 h-6 text-red-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Non-Returnable Items</h2>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Personal care and hygiene products</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Customized or personalized items</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Items marked as "Final Sale" or "Non-Returnable"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Gift cards and vouchers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>Downloaded software or digital products</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* How to Return */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">How to Initiate a Return</h2>
            <ol className="space-y-3 text-slate-700">
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">1.</span>
                <span>Log in to your account and go to "My Orders"</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">2.</span>
                <span>Select the item you wish to return and click "Request Return"</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">3.</span>
                <span>Provide a reason for the return and upload photos if applicable</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">4.</span>
                <span>Our team will review and approve your return request within 24-48 hours</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">5.</span>
                <span>Pack the item securely in its original packaging</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-orange-600 shrink-0">6.</span>
                <span>Our courier partner will pick up the item from your address</span>
              </li>
            </ol>
          </section>

          {/* Important Notes */}
          <section className="bg-orange-50 rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Important Notes</h2>
            <ul className="space-y-2 text-slate-700 text-sm">
              <li>• Refunds will be processed within 7-10 business days after receiving the returned item</li>
              <li>• Return shipping is free for defective or incorrect items</li>
              <li>• Customer is responsible for return shipping costs for change of mind returns</li>
              <li>• Items must be returned in the same condition as received</li>
              <li>• Furniture returns may incur a restocking fee if assembly was attempted</li>
            </ul>
          </section>

          {/* Contact Section */}
          <section className="border-t pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Need Help?</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have any questions about our return policy, please don't hesitate to contact us:
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-slate-700"><strong>Phone:</strong> +91 73532 97211</p>
              <p className="text-slate-700"><strong>Email:</strong> info@brightplanetventures.com</p>
              <p className="text-slate-700"><strong>Hours:</strong> Monday - Saturday, 9:00 AM - 6:00 PM IST</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default ReturnPolicy;
